import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

export type StatTone = 'default' | 'good' | 'warning' | 'critical';

export interface StatTileProps {
    label: string;
    value: string | number;
    sub?: string;
    tone?: StatTone;
    /** Goal figure shown under the meter; a tile with a target renders a progress meter. */
    target?: number;
    /** Progress toward the target as a 0–100 percentage; clamped when out of range. */
    meter?: number;
    /** Overrides the default "Target: {target}" caption, e.g. when the meter tracks a derived metric. */
    targetLabel?: string;
    /**
     * Unit indicator rendered just before the value, muted and smaller — e.g. the currency symbol
     * on a money tile. Without it a bare "12,400.00" beside a count tile reads as a quantity.
     */
    unit?: string;
}

const TONE_COLOR: Record<StatTone, string> = {
    default: 'text.primary',
    good: 'success.main',
    warning: 'warning.main',
    critical: 'error.main',
};

export default function StatTile({ label, value, sub, tone = 'default', target, meter, targetLabel, unit }: StatTileProps) {
    const { t } = useTranslation('dashboard');
    return (
        <Box data-tone={tone} sx={{ p: 1.5, minWidth: 'min-content', flex: '1 1 96px', maxWidth: '100%', overflowWrap: 'anywhere' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', overflowWrap: 'break-word', wordBreak: 'normal', hyphens: 'auto' }}>
                {label}
            </Typography>
            <Typography sx={{ fontFamily: 'Archivo, sans-serif', fontWeight: 700, fontSize: { xs: 22, sm: 28 }, lineHeight: 1.1, color: TONE_COLOR[tone], overflowWrap: 'normal' }}>
                {unit && (
                    <Box component="span" sx={{ fontSize: { xs: 15, sm: 18 }, fontWeight: 500, color: 'text.secondary', mr: 0.5 }}>
                        {unit}
                    </Box>
                )}
                {value}
            </Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
            {meter != null && (
                <Box sx={{ mt: 0.75 }}>
                    <Box
                        data-testid="stat-tile-meter"
                        sx={{ height: 4, borderRadius: 2, bgcolor: 'action.hover', overflow: 'hidden' }}
                    >
                        <Box
                            sx={{
                                height: '100%',
                                width: `${Math.max(0, Math.min(100, meter))}%`,
                                bgcolor: TONE_COLOR[tone],
                                borderRadius: 2,
                            }}
                        />
                    </Box>
                    {target != null && (
                        <Typography variant="caption" color="text.secondary">
                            {targetLabel ?? t('charts.target', { target })}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}
